/**
 * Pure helpers for the V3 Credin ticket screen (`CredinTicketV3Page`).
 */
import { getIncomingDebinAccountName } from "util/accounts";
import formatLegacyWebDateTimeString from "util/formatLegacyWebDateTimeString";
import * as i18n from "util/i18n";
import { getGeneralTicketItem } from "util/transaction";

import { getCredinCurrencyLabel } from "v3/pages/transfers/createCredin";

/**
 * CBU and CVU recipients are submitted as separate transactions, so the activity id tells which
 * flavour of CREDIN the ticket belongs to.
 */
const CREDIN_CVU_ACTIVITY_ID = "credinCVU.sentRequest.send";

const CREDIN_TITLE_KEY = "requestCredin.title";
const CREDIN_CVU_TITLE_KEY = "credin.ticket.title.cvu";

/**
 * @param {object|undefined|null} summary
 * @returns {string} i18n key for the ticket title
 */
export function getCredinTicketTitleKey(summary) {
    return summary?.idActivity === CREDIN_CVU_ACTIVITY_ID ? CREDIN_CVU_TITLE_KEY : CREDIN_TITLE_KEY;
}

/**
 * @param {object|undefined|null} summary
 * @returns {string}
 */
export function getCredinTicketTitle(summary) {
    return i18n.get(getCredinTicketTitleKey(summary));
}

/**
 * @param {string} id
 * @returns {string}
 */
export function getCredinTicketLabel(id) {
    if (id === "goToRequestCredin") {
        return i18n.get("credin.ticket.goToRequestCredin");
    }
    return i18n.get(`credin.confirmation.${id}`);
}

/**
 * @param {string|undefined|null} recipientDocumentType
 * @returns {string}
 */
function resolveCredinDocumentTypeLabel(recipientDocumentType) {
    if (!recipientDocumentType) {
        return "";
    }
    return i18n.get(`documentType.label.${String(recipientDocumentType).toUpperCase()}`, recipientDocumentType);
}

/**
 * Resolves debit account label from summary account id and Redux accounts list.
 *
 * @param {object|undefined|null} summary
 * @param {Array<object>} accounts
 * @returns {string}
 */
export function resolveCredinDebitAccountLabel(summary, accounts) {
    if (!summary?.account || !Array.isArray(accounts) || accounts.length === 0) {
        return "-";
    }

    const loggedUserAccount = accounts.find((account) => String(summary.account).includes(String(account?.idProduct)));

    if (!loggedUserAccount) {
        return "-";
    }

    return getIncomingDebinAccountName(loggedUserAccount);
}

const LEGACY_CANCELLED_TIME_PATTERN = /^\d{2}\/\d{2}\/\d{4}/;

/**
 * Same fields as legacy `TransactionTicket` Cancelado Por (`/confirmCredin/transaction`).
 *
 * @param {object|undefined|null} summary
 * @returns {{ userFullName: string, creationDateTime: string|undefined }|undefined}
 */
export function mapCredinCancelledBy(summary) {
    if (summary?.idTransactionStatus !== "CANCELLED") {
        return undefined;
    }

    const userFullName = summary.returnCode
        ? i18n.get(`returnCode.${summary.returnCode}`)
        : summary.cancelledUser || summary.data?.cancelledUser || "";

    if (!userFullName) {
        return undefined;
    }

    const creationDateTime = summary.returnCode
        ? summary.submitDateTimeAsString
        : summary.cancelledTime || summary.data?.cancelledTime || undefined;

    return {
        userFullName,
        creationDateTime,
    };
}

/**
 * `cancelledTime` is stored as `dd/MM/yyyy HH:mm:ss` and legacy prints it as-is.
 *
 * @param {string|number|Date|null|undefined} input
 * @returns {string}
 */
export function formatCredinTicketSignatureDate(input) {
    if (typeof input === "string" && LEGACY_CANCELLED_TIME_PATTERN.test(input.trim())) {
        return input.trim();
    }
    return formatLegacyWebDateTimeString(input);
}

export function mapCredinSummaryToTicketData(summary, accounts) {
    if (!summary) {
        return {};
    }

    const conceptCode = summary.concept;
    const conceptLabel = conceptCode ? i18n.get(`transfers.concept.${conceptCode}`, conceptCode) : "-";

    return {
        idTransaction: summary.idTransaction,
        idTransactionStatus: summary.idTransactionStatus,
        submitDateTime: summary.submitDateTime,
        creationDate: summary.creationDate ?? summary.creationDateTime,
        creationDateTime: summary.creationDateTime ?? summary.creationDate,
        modificationDateTime: summary.modificationDateTime || "",
        concept: conceptLabel,
        description: summary.description || "",
        idCredin: summary.idCredin || "",
        debitAccountLabel: resolveCredinDebitAccountLabel(summary, accounts),
        signatures: summary.signatures,
        creatorFullName: summary.creatorFullName,
        voucherErrorCode: summary.voucherErrorCode || summary.data?.voucherErrorCode || "",
        voucherErrorMessage:
            summary.voucherErrorMessage ||
            summary.data?.voucherErrorMessage ||
            summary.voucherServiceMessage ||
            summary.data?.voucherServiceMessage ||
            "",
        responseIBS: summary.responseIBS || summary.data?.responseIBS || "",
    };
}

/**
 * @param {object} recipient
 * @returns {object}
 */
export function mapCredinRecipientToTicketInput(recipient) {
    if (!recipient) {
        return {};
    }

    const { amount = {}, recipientDocumentType, recipientDocumentNumber } = recipient;
    const { currency, quantity } = amount;
    const documentTypeLabel = resolveCredinDocumentTypeLabel(recipientDocumentType);
    const documentLabel =
        recipientDocumentNumber && documentTypeLabel
            ? `${documentTypeLabel} ${recipientDocumentNumber}`.trim()
            : recipientDocumentNumber || undefined;

    return {
        recipientName: recipient.recipientName,
        recipientDocumentType,
        recipientDocumentNumber,
        documentLabel,
        accountNumber: recipient.accountNumber,
        cbu: recipient.cbu,
        localBank: recipient.localBank,
        reference: recipient.reference,
        comprobante: recipient.comprobante,
        id: recipient.id,
        amount: quantity,
        currency: getCredinCurrencyLabel(currency),
    };
}

/**
 * Normalizes legacy summary recipients (object or array) into ticket inputs.
 *
 * @param {object|Array<object>|undefined|null} recipients
 * @returns {Array<object>}
 */
export function mapCredinSummaryRecipients(recipients) {
    if (!recipients) {
        return [];
    }

    if (Array.isArray(recipients)) {
        return recipients.map(mapCredinRecipientToTicketInput);
    }

    return [mapCredinRecipientToTicketInput(recipients)];
}

/**
 * Builds PDF ticket body items mirroring legacy `CredinPrepareConfirmation.buildPdf`.
 *
 * @param {object} summary
 * @param {object} recipient
 * @param {string} accountName
 * @returns {Array<object>}
 */
export function buildCredinTicketPdfItems(summary, recipient, accountName) {
    const {
        recipientName,
        recipientDocumentType,
        recipientDocumentNumber,
        amount: { currency, quantity } = {},
        accountNumber,
        cbu,
        localBank,
        reference,
        comprobante,
        id,
    } = recipient;

    return [
        getGeneralTicketItem("negotiate.transaction.date.label", summary?.submitDateTime, "datefulltime"),
        getGeneralTicketItem(
            "requestCredin.confirm.table.header.ticket",
            summary?.idTransaction?.substring(0, 8)?.toUpperCase(),
            "string",
        ),
        getGeneralTicketItem("requestCredin.confirm.table.header.debitAccount", accountName, "string"),
        getGeneralTicketItem("credin.transaction.id.credin", summary.idCredin || "", "string"),
        getGeneralTicketItem(
            `requestCredin.confirm.table.header.type${cbu?.startsWith("000") ? "cvu" : "cbu"}`,
            cbu,
            "string",
        ),
        getGeneralTicketItem("requestCredin.confirm.table.header.creditAccount.label", accountNumber, "string"),
        getGeneralTicketItem(
            "requestCredin.confirm.table.header.creditAccount.cuit",
            `${resolveCredinDocumentTypeLabel(recipientDocumentType)} ${recipientDocumentNumber}`.trim(),
            "string",
        ),
        getGeneralTicketItem("requestCredin.confirm.table.header.creditAccount.owner", recipientName, "string"),
        getGeneralTicketItem(
            `requestCredin.confirm.table.header.creditAccount.${cbu?.startsWith("000") ? "entity" : "bank"}`,
            `${localBank && localBank !== "000" ? localBank : "-"}`,
            "string",
        ),
        getGeneralTicketItem("requestCredin.confirm.table.header.creditAccount.reference", reference || "", "string"),
        getGeneralTicketItem("requestCredin.confirm.table.header.amount", { quantity, currency }, "amountFrequency"),
        getGeneralTicketItem("requestCredin.confirm.table.header.ticket", comprobante ?? id, "string"),
        getGeneralTicketItem(
            "requestCredin.confirm.table.header.concept",
            i18n.get(`transfers.concept.${summary.concept}`),
            "string",
        ),
        getGeneralTicketItem("requestCredin.confirm.table.header.description", summary.description || "", "string"),
        { key: "", value: "" },
    ];
}
